// SPA GS-Admin — console d'exploitation ETL / catalogue. Layout @gs/ui, habillage
// traduit (@gs/i18n, namespace 'admin'). registerApiAuth DANS le rendu (synchrone).
// Garde CLIENT (GET /api/auth/me -> AccessGate) = UX ; l'autorite reelle est serveur
// (requireAdmin = requireAuth + requireRole bc_key ADM, minRole ADMIN).
// Ecrans partages : GsLoginScreen (login) + AccessGate (refus) — JAMAIS d'ecran bricole.
import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { GsLayout, GsHeader, GsSidebar, GsMobileBottomNav, GsMoreMenuSheet, GS_MORE_PATH, useDarkMode, GsErrorBoundary, GsErrorPage, GsVersionBanner, BrandingProvider, GsLoginScreen, AccessGate } from '@gs/ui';
import type { SidebarItem, AccessGateKind } from '@gs/ui';
import { getConfig, GS_APP_URLS } from '@gs/config/browser';
import { BarChart3, CalendarClock, Database, GitBranch, GitCompare, HeartPulse, Layers, LayoutDashboard, LayoutGrid, Menu, Palette, RefreshCw, ScrollText, ToggleRight } from 'lucide-react';
import { useAuth } from '@gs/auth';
import type { TFunction } from 'i18next';

import { registerApiAuth, clearApiAuth } from './services/api';
import { useTranslation } from './i18n';
import { useMe, type SystemCRole } from './hooks/useMe';
import { resolveAccessGate, adminRoleOf } from './lib/resolveAccessGate';

import DashboardPage from './pages/DashboardPage';
import SyncManagerPage from './pages/SyncManagerPage';
import LogsPage from './pages/LogsPage';
import AccessMovedCard from './pages/AccessMovedCard';
import CatalogSofiPage from './pages/CatalogSofiPage';
import { SchemaPage } from './pages/SchemaPage';
import PipelinePage from './pages/PipelinePage';
import DesignPage from './pages/DesignPage';
import InvSitesPage from './pages/InvSitesPage';
import DataArchitecturePage from './pages/DataArchitecturePage';
import SanteDuParcPage from './pages/SanteDuParcPage';
import EnvironnementsPage from './pages/EnvironnementsPage';
import CalendrierConservation from './pages/CalendrierConservation';

const LineagePage = lazy(() => import('./pages/LineagePage'));

const ADMIN_APP_NAME = 'GS-Admin';

/** Titre d'onglet : section + suffixe d'app (patron gest), consomme par GsHeader. */
function pageTitle(pathname: string, t: TFunction): string {
  return `${adminSectionTitle(pathname, t)} — ${ADMIN_APP_NAME}`;
}

function adminSectionTitle(pathname: string, t: TFunction): string {
  if (pathname.startsWith('/syncs')) return t('admin:pageTitle.syncs');
  if (pathname.startsWith('/logs')) return t('admin:pageTitle.logs');
  if (pathname.startsWith('/catalogue-sofi')) return t('admin:pageTitle.catalogSofi');
  if (pathname.startsWith('/schema')) return t('admin:pageTitle.schema');
  if (pathname.startsWith('/architecture-donnees')) return t('admin:pageTitle.dataArchitecture');
  if (pathname.startsWith('/pipeline')) return t('admin:pageTitle.pipeline');
  if (pathname.startsWith('/design')) return t('admin:pageTitle.design');
  if (pathname.startsWith('/lineage')) return t('admin:pageTitle.lineage');
  if (pathname.startsWith('/sites-inventaire')) return t('admin:pageTitle.invSites');
  if (pathname.startsWith('/utilisateurs')) return t('admin:pageTitle.users');
  if (pathname.startsWith('/sante-du-parc')) return t('admin:pageTitle.fleetHealth');
  if (pathname.startsWith('/environnements')) return t('admin:pageTitle.environments');
  if (pathname.startsWith('/calendrier-de-conservation')) return t('admin:pageTitle.retentionSchedule');
  return t('admin:pageTitle.dashboard'); // '/' et toute route inconnue (redirigee vers '/')
}

function roleLabel(role: SystemCRole | null, t: TFunction): string {
  switch (role) {
    case 'ADMIN': return t('admin:roles.admin');
    case 'GESTIONNAIRE': return t('admin:roles.manager');
    default: return t('admin:roles.user');
  }
}

/**
 * Textes du refus, par cause. Cles LITTERALES a dessein : une cle construite
 * (t(`...${kind}`)) echappe a i18next-parser, qui la PURGE ensuite du catalogue
 * (keepRemoved:false). moduleNotEntitled n'est jamais emis par admin (pas un module
 * CompanyModules) : il retombe, comme accessPending, sur le texte du refus de role.
 */
function accessGateLabels(kind: AccessGateKind, t: TFunction): { title: string; description: string; retry?: string } {
  switch (kind) {
    case 'notProvisioned':
      return {
        title: t('admin:accessGate.notProvisioned.title'),
        description: t('admin:accessGate.notProvisioned.description'),
      };
    case 'unverifiable':
      return {
        title: t('admin:accessGate.unverifiable.title'),
        description: t('admin:accessGate.unverifiable.description'),
        retry: t('admin:accessGate.retry'),
      };
    default:
      return {
        title: t('admin:accessGate.noRole.title'),
        description: t('admin:accessGate.noRole.description'),
      };
  }
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggle: toggleDark } = useDarkMode();
  const { user, isLoading, login, logout, getAccessToken } = useAuth();
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);

  const navItems: SidebarItem[] = [
    { key: 'dashboard', label: t('admin:nav.dashboard'), icon: LayoutDashboard, path: '/' },
    { key: 'fleet-health', label: t('admin:nav.fleetHealth'), icon: HeartPulse, path: '/sante-du-parc' },
    { key: 'environments', label: t('admin:nav.environments'), icon: Layers, path: '/environnements' },
    { key: 'syncs', label: t('admin:nav.syncs'), icon: RefreshCw, path: '/syncs' },
    { key: 'logs', label: t('admin:nav.logs'), icon: ScrollText, path: '/logs' },
  ];

  const adminNavItems: SidebarItem[] = [
    { key: 'catalog-data', label: t('admin:nav.catalogData'), icon: Database, path: '/schema' },
    { key: 'data-arch', label: t('admin:nav.dataArchitecture'), icon: BarChart3, path: '/architecture-donnees' },
    { key: 'lineage', label: t('admin:nav.lineage'), icon: GitCompare, path: '/lineage' },
    { key: 'pipeline', label: t('admin:nav.pipeline'), icon: GitBranch, path: '/pipeline' },
    { key: 'design', label: t('admin:nav.design'), icon: Palette, path: '/design' },
    { key: 'inv-sites', label: t('admin:nav.invSites'), icon: ToggleRight, path: '/sites-inventaire' },
    { key: 'retention-schedule', label: t('admin:nav.retentionSchedule'), icon: CalendarClock, path: '/calendrier-de-conservation' },
    // Catalogue SOFI accessible via /catalogue-sofi (référence dev ETL — retiré du nav principal)
  ];

  // Zone SUPPORT (UI_STANDARDS §4c) : le lanceur GS, comme ug / gest / inv.
  const supportItems: SidebarItem[] = [
    { key: 'launcher', label: t('admin:nav.launcher'), icon: LayoutGrid, path: GS_APP_URLS.hub, externalUrl: true },
  ];

  const ADMIN_MOBILE_PRIMARY = new Set(['dashboard', 'fleet-health', 'syncs']);
  const mobileNavItems: SidebarItem[] = [
    ...navItems.filter((i) => ADMIN_MOBILE_PRIMARY.has(i.key)),
    { key: 'more', label: t('admin:nav.more'), icon: Menu, path: GS_MORE_PATH },
  ];
  const moreItems: SidebarItem[] = [
    ...navItems.filter((i) => !ADMIN_MOBILE_PRIMARY.has(i.key)),
    ...adminNavItems,
    ...supportItems,
  ];

  // ── Enregistrement SYNCHRONE du token ────────────────────────────────────
  // Appelé pendant le rendu, pas dans useEffect — garantit que le token
  // est disponible avant que TanStack Query monte et lance ses requêtes.
  if (user) {
    registerApiAuth(getAccessToken);
  } else {
    clearApiAuth();
  }

  useEffect(() => {
    return () => { clearApiAuth(); };
  }, []);

  // Verdict /api/auth/me — CONFORT D'AFFICHAGE, JAMAIS UNE GARDE. L'autorite reste serveur.
  const me = useMe(!!user);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <GsLoginScreen
        appName="Groupe Signalisation"
        message={t('admin:login.message')}
        loginLabel={t('admin:login.cta')}
        logoSrc="/logo-gs.png"
        logoSrcDark="/logo-gs-white.png"
        onLogin={() => void login()}
      />
    );
  }

  // Anti-flash : tant que /api/auth/me n'a rendu NI profil NI erreur, on n'affiche
  // NI le shell NI le refus. `isPending` et non `isLoading` : une requete en attente
  // sans reseau n'est pas « en chargement » pour TanStack, et laisserait passer le shell.
  if (me.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  const gate = resolveAccessGate(me);
  if (gate) {
    // Porte PARTAGEE (@gs/ui) — refus serveur + unverifiable. Les textes viennent du
    // catalogue de l'app (props) : @gs/ui n'appelle jamais t().
    return (
      <AccessGate
        kind={gate}
        email={user.email}
        onLogout={() => void logout()}
        onRetry={gate === 'unverifiable' ? () => { void me.refetch(); } : undefined}
        retrying={me.isFetching}
        labels={{
          ...accessGateLabels(gate, t),
          logout: t('admin:chrome.logout'),
        }}
      />
    );
  }

  const role = adminRoleOf(me.data);

  return (
    // Theming runtime ADR-059 (rollout post-pilote ug) : branding de la company
    // de l'user. Fail-soft : 404/erreur = fallback :root GS 2026 (no-op visuel).
    <BrandingProvider enabled={!!user}>
    <GsLayout
      mainId="contenu-principal"
      skipLinkLabel={t('admin:chrome.skipToContent')}
      header={<GsHeader pageTitle={pageTitle(location.pathname, t)} />}
      sidebar={
        <GsSidebar
          variant="light"
          headerLogoSrc="/logo-gs.png"
          headerLogoSrcDark="/logo-gs-white.png"
          homeLabel={t('admin:chrome.backToHome')}
          appName={ADMIN_APP_NAME}
          asideLabel={t('admin:chrome.sidebarAria')}
          navLabel={t('admin:chrome.navAria')}
          mainSectionLabel={t('admin:chrome.navigation')}
          adminSectionLabel={t('admin:chrome.adminSection')}
          items={navItems}
          adminItems={adminNavItems}
          supportItems={supportItems}
          isAdmin={role === 'ADMIN'}
          activePath={location.pathname}
          onNavigate={(path: string) => navigate(path)}
          LinkComponent={Link}
          bottomUser={{
            name: user.name,
            email: user.email,
            role: roleLabel(role, t),
            initials: user.initials,
          }}
          isDark={isDark}
          onToggleDark={toggleDark}
          onLogout={() => void logout()}
          appVersion="0.1.0"
          collapseLabel={t('admin:chrome.collapseSidebar')}
          expandLabel={t('admin:chrome.expandSidebar')}
          userMenuLabels={{
            lightMode: t('admin:chrome.lightMode'),
            darkMode: t('admin:chrome.darkMode'),
            language: t('admin:chrome.language'),
            logout: t('admin:chrome.logout'),
          }}
        />
      }
      mobileNav={
        <GsMobileBottomNav
          items={mobileNavItems}
          activePath={location.pathname}
          onNavigate={(path: string) => {
            if (path === GS_MORE_PATH) { setMoreOpen(true); return; }
            navigate(path);
          }}
          navLabel={t('admin:nav.mobileNav')}
        />
      }
    >
      <GsVersionBanner apiBaseUrl={getConfig().apiUrl} />
      <GsErrorBoundary>
        <Suspense
          fallback={
            <div className="flex min-h-[40vh] items-center justify-center p-6 text-sm text-slate-500">
              {t('admin:chrome.loading')}
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/utilisateurs" element={<AccessMovedCard />} />
            <Route path="/sante-du-parc" element={<SanteDuParcPage />} />
            <Route path="/environnements" element={<EnvironnementsPage />} />
            <Route path="/syncs" element={<SyncManagerPage />} />
            <Route path="/logs" element={<LogsPage />} />
            <Route path="/catalogue-sofi" element={<CatalogSofiPage />} />
            <Route path="/schema" element={<SchemaPage />} />
            <Route path="/architecture-donnees" element={<DataArchitecturePage />} />
            <Route path="/lineage" element={<LineagePage />} />
            <Route path="/pipeline" element={<PipelinePage />} />
            <Route path="/design" element={<DesignPage />} />
            <Route path="/sites-inventaire" element={<InvSitesPage />} />
            <Route path="/calendrier-de-conservation" element={<CalendrierConservation />} />
            <Route path="/error/:code" element={<GsErrorPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </GsErrorBoundary>
    </GsLayout>
    <GsMoreMenuSheet
      open={moreOpen}
      onClose={() => setMoreOpen(false)}
      items={moreItems}
      onNavigate={(path: string) => navigate(path)}
      title={t('admin:nav.more')}
      closeLabel={t('admin:nav.closeMore')}
      onLogout={() => void logout()}
      logoutLabel={t('admin:chrome.logout')}
    />
    </BrandingProvider>
  );
}
